import * as CookieConsent from 'vanilla-cookieconsent';
import xdk from '@accedo/xdk-core';
import TagManager from 'react-gtm-module';

import 'vanilla-cookieconsent/dist/cookieconsent.css';
import './consent.scss';

const isClient = typeof window !== 'undefined';

export const CONSENT_CATEGORIES = {
  NECESSARY: 'necessary',
  ANALYTICS: 'analytics',
  ADVERTISING: 'advertising',
};

const GOOGLE_CONSENT_TYPES = {
  AD_STORAGE: 'ad_storage',
  AD_USER_DATA: 'ad_user_data',
  AD_PERSONALIZATION: 'ad_personalization',
  ANALYTICS_STORAGE: 'analytics_storage',
  FUNCTIONALITY_STORAGE: 'functionality_storage',
  PERSONALIZATION_STORAGE: 'personalization_storage',
  SECURITY_STORAGE: 'security_storage',
};

const CONSENT_COOKIE_NAME = 'sensical_cookie_consent';
const CONSENT_REVISION = 1;
const callbacks = [];

let gtmInitialized = false;
let consentInitialized = false;
let consentReady = false;
let activeGtmId;
let consentRequired = true;

const getSharedCookieDomain = () => {
  if (!isClient) {
    return undefined;
  }

  return window.location.hostname.endsWith('sensical.tv')
    ? '.sensical.tv'
    : window.location.hostname;
};

const gtag = (...args) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
};

const hasCategory = category => {
  if (!isClient) {
    return false;
  }

  if (!consentRequired) {
    return true;
  }

  return CookieConsent.acceptedCategory(category);
};

export const hasAnalyticsConsent = () =>
  hasCategory(CONSENT_CATEGORIES.ANALYTICS);

export const hasAdvertisingConsent = () =>
  hasCategory(CONSENT_CATEGORIES.ADVERTISING);

export const isConsentReady = () => consentReady;

const setDefaultGoogleConsent = () => {
  if (!isClient) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || gtag;
  window.gtag('consent', 'default', {
    [GOOGLE_CONSENT_TYPES.AD_STORAGE]: 'denied',
    [GOOGLE_CONSENT_TYPES.AD_USER_DATA]: 'denied',
    [GOOGLE_CONSENT_TYPES.AD_PERSONALIZATION]: 'denied',
    [GOOGLE_CONSENT_TYPES.ANALYTICS_STORAGE]: 'denied',
    [GOOGLE_CONSENT_TYPES.FUNCTIONALITY_STORAGE]: 'granted',
    [GOOGLE_CONSENT_TYPES.PERSONALIZATION_STORAGE]: 'denied',
    [GOOGLE_CONSENT_TYPES.SECURITY_STORAGE]: 'granted',
  });
};

const updateGoogleConsent = () => {
  if (!isClient) {
    return;
  }

  const analyticsGranted = hasAnalyticsConsent() ? 'granted' : 'denied';
  const advertisingGranted = hasAdvertisingConsent() ? 'granted' : 'denied';

  window.gtag('consent', 'update', {
    [GOOGLE_CONSENT_TYPES.AD_STORAGE]: advertisingGranted,
    [GOOGLE_CONSENT_TYPES.AD_USER_DATA]: 'denied',
    [GOOGLE_CONSENT_TYPES.AD_PERSONALIZATION]: 'denied',
    [GOOGLE_CONSENT_TYPES.ANALYTICS_STORAGE]: analyticsGranted,
    [GOOGLE_CONSENT_TYPES.FUNCTIONALITY_STORAGE]: 'granted',
    [GOOGLE_CONSENT_TYPES.PERSONALIZATION_STORAGE]: 'denied',
    [GOOGLE_CONSENT_TYPES.SECURITY_STORAGE]: 'granted',
  });
};

const initializeGtm = () => {
  if (!activeGtmId || gtmInitialized || !isClient) {
    return;
  }

  if (consentRequired && !hasAnalyticsConsent() && !hasAdvertisingConsent()) {
    return;
  }

  TagManager.initialize({ gtmId: activeGtmId });
  gtmInitialized = true;
};

const notifyConsentChange = () => {
  callbacks.forEach(callback => callback());
};

const handleConsentUpdate = () => {
  updateGoogleConsent();
  initializeGtm();
  notifyConsentChange();
};

export const onConsentChange = callback => {
  if (typeof callback === 'function') {
    callbacks.push(callback);
  }
};

export const showConsentPreferences = () => {
  if (isClient && consentInitialized) {
    CookieConsent.showPreferences();
  }
};

const getConsentConfig = () => ({
  mode: 'opt-in',
  revision: CONSENT_REVISION,
  autoClearCookies: true,
  disablePageInteraction: true,
  root: 'body',
  cookie: {
    name: CONSENT_COOKIE_NAME,
    domain: getSharedCookieDomain(),
    path: '/',
    secure: true,
    sameSite: 'Lax',
    expiresAfterDays: 180,
    useLocalStorage: false,
  },
  guiOptions: {
    consentModal: {
      layout: 'box',
      position: 'bottom right',
      equalWeightButtons: false,
    },
    preferencesModal: {
      layout: 'box',
      position: 'right',
      equalWeightButtons: false,
    },
  },
  onFirstConsent: handleConsentUpdate,
  onConsent: handleConsentUpdate,
  onChange: handleConsentUpdate,
  categories: {
    [CONSENT_CATEGORIES.NECESSARY]: {
      enabled: true,
      readOnly: true,
    },
    [CONSENT_CATEGORIES.ANALYTICS]: {
      enabled: false,
      autoClear: {
        cookies: [
          { name: /^_ga/, domain: getSharedCookieDomain() },
          { name: '_gid', domain: getSharedCookieDomain() },
          { name: /^_gat/, domain: getSharedCookieDomain() },
          { name: /^_fbp/, domain: getSharedCookieDomain() },
        ],
      },
    },
    [CONSENT_CATEGORIES.ADVERTISING]: {
      enabled: false,
      autoClear: {
        cookies: [
          { name: /^_gcl_/, domain: getSharedCookieDomain() },
          { name: /^IDE$/, domain: '.doubleclick.net' },
          { name: /^test_cookie$/, domain: '.doubleclick.net' },
        ],
      },
    },
  },
  language: {
    default: 'en',
    translations: {
      en: {
        consentModal: {
          title: 'Privacy and cookie choices',
          description:
            'Sensical uses required technology to run this service and record limited video playback heartbeats. With your permission, Cricket Media and Sensical may also use optional analytics and advertising cookies to improve the service and support ad delivery.',
          acceptAllBtn: 'Allow optional cookies',
          acceptNecessaryBtn: 'Necessary only',
          showPreferencesBtn: 'Manage choices',
        },
        preferencesModal: {
          title: 'Cookie preferences',
          acceptAllBtn: 'Allow optional cookies',
          acceptNecessaryBtn: 'Necessary only',
          savePreferencesBtn: 'Save choices',
          closeIconLabel: 'Close cookie preferences',
          sections: [
            {
              title: 'Your choices',
              description:
                'Sensical is operated by Cricket Media and is designed for families. Optional cookies stay off unless accepted. You can change these choices at any time from Cookie preferences.',
            },
            {
              title: 'Strictly necessary cookies',
              description:
                'Required for core service operation, security, age-gate/session behavior, playback, limited video heartbeat measurement, and account access. These cannot be turned off here.',
              linkedCategory: CONSENT_CATEGORIES.NECESSARY,
            },
            {
              title: 'Analytics cookies',
              description:
                'Allow optional measurement beyond the required video heartbeat, including app usage, playback performance, and registration events. Optional analytics cookies are disabled until accepted.',
              linkedCategory: CONSENT_CATEGORIES.ANALYTICS,
              cookieTable: {
                headers: {
                  name: 'Cookie',
                  domain: 'Service',
                  description: 'Purpose',
                  expiration: 'Expiration',
                },
                body: [
                  {
                    name: '_ga, _gid, _gat',
                    domain: 'Google Analytics / GTM',
                    description:
                      'Used only when analytics cookies are accepted to measure site and app usage.',
                    expiration: 'Varies',
                  },
                ],
              },
            },
            {
              title: 'Advertising cookies',
              description:
                'Allow ad delivery and measurement cookies for video advertising and DAI support. Personalized advertising and ad user-data consent remain disabled by default in Google Consent Mode.',
              linkedCategory: CONSENT_CATEGORIES.ADVERTISING,
              cookieTable: {
                headers: {
                  name: 'Cookie',
                  domain: 'Service',
                  description: 'Purpose',
                  expiration: 'Expiration',
                },
                body: [
                  {
                    name: '_gcl_*, IDE, test_cookie',
                    domain: 'Google advertising services',
                    description:
                      'Used only when advertising cookies are accepted for ad delivery and measurement.',
                    expiration: 'Varies',
                  },
                ],
              },
            },
            {
              title: 'More information',
              description:
                'For details, please review the Sensical Privacy Policy and Children’s Privacy Policy.',
            },
          ],
        },
      },
    },
  },
});

export const initializeConsent = async ({ gtmId } = {}) => {
  if (!isClient) {
    return;
  }

  activeGtmId = gtmId;

  if (xdk.platform !== 'workstation') {
    consentRequired = false;
    consentReady = true;
    initializeGtm();
    notifyConsentChange();
    return;
  }

  setDefaultGoogleConsent();

  if (!consentInitialized) {
    await CookieConsent.run(getConsentConfig());
    consentInitialized = true;
  }

  consentReady = true;
  handleConsentUpdate();

  window.SensicalCookieConsent = {
    showPreferences: showConsentPreferences,
    hasAnalyticsConsent,
    hasAdvertisingConsent,
  };
};
